import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Check, CreditCard, Smartphone, Users, Star, Zap, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const brl = (v) => `R$${Number(v).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;

const PLANS = [
  {
    id: 'plan_basico',
    nome: 'Básico',
    preco: 69,
    max_funcs: 1,
    label: '1 funcionário',
    icon: Zap,
    features: ['1 funcionário', 'Orçamentos ilimitados', 'OCR de cupom fiscal', 'Link público', 'Relatórios', 'Suporte WhatsApp'],
  },
  {
    id: 'plan_profissional',
    nome: 'Profissional',
    preco: 102,
    max_funcs: 5,
    label: '2 a 5 funcionários',
    icon: Users,
    popular: true,
    features: ['Até 5 funcionários', 'Orçamentos ilimitados', 'OCR de cupom fiscal', 'Link público', 'Relatórios', 'Suporte WhatsApp'],
  },
  {
    id: 'plan_empresarial',
    nome: 'Empresarial',
    preco: 164,
    max_funcs: 10,
    label: '6 a 10 funcionários',
    icon: Building2,
    features: ['Até 10 funcionários', 'Orçamentos ilimitados', 'OCR de cupom fiscal', 'Link público', 'Relatórios', 'Suporte WhatsApp'],
  },
];

export default function Subscribe() {
  const { user, refreshUser, trialDaysLeft, isTrialExpired } = useAuth();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(PLANS[0]);
  const [payMethod, setPayMethod] = useState('pix');
  const [loading, setLoading] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  if (user?.subscription_status === 'active' && !showSuccess) {
    return (
      <div className="max-w-md mx-auto text-center py-16 space-y-4">
        <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
          <Check size={28} className="text-green-400" />
        </div>
        <h2 className="font-heading text-2xl font-bold text-white">Você já é Pro!</h2>
        <p className="text-zinc-400">Sua assinatura está ativa.</p>
        <button onClick={() => navigate('/')} className="bg-orange-500 hover:bg-orange-400 text-white font-semibold px-6 py-2.5 rounded-xl transition-colors">
          Voltar ao início
        </button>
      </div>
    );
  }

  const daysLeft = trialDaysLeft();
  const expired = isTrialExpired();

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      if (payMethod === 'pix') {
        const res = await api.post('/subscriptions/checkout/pix', { plan_id: selectedPlan.id, employee_count: selectedPlan.max_funcs });
        setPixData(res.data);
 } else {
        const res = await api.post('/subscriptions/checkout/card', { plan_id: selectedPlan.id, employee_count: selectedPlan.max_funcs });
        if (res.data && res.data.init_point) {
          window.location.href = res.data.init_point;
        } else {
          setShowSuccess(true);
          await refreshUser();
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao processar pagamento');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPix = async () => {
    try {
      await api.post('/subscriptions/checkout/card', { plan_id: selectedPlan.id });
      setShowSuccess(true);
      await refreshUser();
    } catch {
      toast.error('Erro ao confirmar pagamento');
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in py-4">
      <div className="text-center">
        <h1 className="font-heading text-2xl font-bold text-white">
          {expired ? 'Seu trial expirou' : `${daysLeft} dias restantes no trial`}
        </h1>
        <p className="text-zinc-400 mt-1">Escolha o plano ideal para o seu negócio</p>
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {PLANS.map((plan) => {
          const Icon = plan.icon;
          const isSelected = selectedPlan.id === plan.id;
          return (
            <button
              key={plan.id}
              data-testid={`plan-${plan.id}`}
              onClick={() => { setSelectedPlan(plan); setPixData(null); }}
              className={`relative text-left p-5 rounded-2xl border transition-all space-y-4 ${
                isSelected
                  ? 'border-orange-500 bg-orange-500/10 shadow-[0_0_20px_rgba(249,115,22,0.2)]'
                  : 'border-zinc-800 bg-zinc-900 hover:border-zinc-700'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-[10px] font-bold px-3 py-0.5 rounded-full uppercase tracking-wide flex items-center gap-1">
                  <Star size={10} /> Mais popular
                </div>
              )}
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isSelected ? 'bg-orange-500' : 'bg-zinc-800'}`}>
                <Icon size={18} className={isSelected ? 'text-white' : 'text-zinc-400'} />
              </div>
              <div>
                <div className="font-heading font-bold text-white text-lg">{plan.nome}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{plan.label}</div>
              </div>
              <div>
                <span className="text-3xl font-black text-orange-400 font-mono">R${plan.preco}</span>
                <span className="text-zinc-500 text-sm">/mês</span>
              </div>
              <ul className="space-y-1.5">
                {plan.features.map(f => (
                  <li key={f} className="flex items-center gap-2 text-xs text-zinc-400">
                    <Check size={11} className="text-green-400 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>

      {/* Payment method */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5 space-y-4">
        <h3 className="font-medium text-white">Forma de pagamento</h3>
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => setPayMethod('pix')}
            className={`flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-medium transition-all ${
              payMethod === 'pix' ? 'border-orange-500 bg-orange-500/10 text-orange-400' : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'
            }`}>
            <Smartphone size={17} /> PIX
          </button>
          <button onClick={() => setPayMethod('card')}
            className={`flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-medium transition-all ${
              payMethod === 'card' ? 'border-orange-500 bg-orange-500/10 text-orange-400' : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'
            }`}>
            <CreditCard size={17} /> Cartão
          </button>
        </div>

        {/* Selected plan summary */}
        <div className="bg-zinc-800/50 rounded-xl p-3 flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-white">Plano {selectedPlan.nome}</div>
            <div className="text-xs text-zinc-500">{selectedPlan.label}</div>
          </div>
          <div className="text-xl font-black text-orange-400 font-mono">R${selectedPlan.preco}/mês</div>
        </div>

        {pixData && (
          <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4 space-y-3">
            <div className="text-sm font-medium text-white text-center">Pague via PIX</div>
            <div className="bg-zinc-900 rounded-lg p-3 text-xs font-mono text-zinc-300 break-all select-all">{pixData.qr_code}</div>
            <button onClick={() => navigator.clipboard?.writeText(pixData.qr_code)}
              className="w-full text-sm text-orange-400 border border-orange-500/30 hover:bg-orange-500/10 py-2 rounded-lg transition-colors">
              Copiar código PIX
            </button>
            <p className="text-xs text-zinc-500 text-center">Simulação — aguardando confirmação</p>
            <button onClick={handleConfirmPix}
              className="w-full bg-green-600 hover:bg-green-500 text-white font-semibold py-2 rounded-xl text-sm transition-colors">
              Verificar pagamento (simulação)
            </button>
          </div>
        )}

        {!pixData && (
          <button data-testid="subscribe-btn" onClick={handleSubscribe} disabled={loading}
            className="w-full bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white font-bold py-3 rounded-xl transition-colors">
            {loading ? 'Processando...' : `Assinar Plano ${selectedPlan.nome} — R$${selectedPlan.preco}/mês`}
          </button>
        )}
        <p className="text-xs text-zinc-500 text-center">Integração Mercado Pago — simulação para desenvolvimento</p>
      </div>

      {/* Payment success notification */}
      {showSuccess && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-sm w-full text-center space-y-5 shadow-2xl animate-fade-in">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto ring-4 ring-green-500/30">
              <Check size={36} className="text-green-400" />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-bold text-white">Assinatura Ativada!</h2>
              <p className="text-zinc-400 text-sm mt-2">
                Bem-vindo ao OrcFácil Pro!<br />
                Plano <span className="text-orange-400 font-semibold">{selectedPlan.nome}</span> · {selectedPlan.label}
              </p>
            </div>
            <div className="bg-zinc-800/50 rounded-xl p-3">
              <div className="text-xs text-zinc-500 mb-1">VALOR DA ASSINATURA</div>
              <div className="text-2xl font-black text-orange-400 font-mono">R${selectedPlan.preco}/mês</div>
            </div>
            <button data-testid="success-start-btn" onClick={() => navigate('/')}
              className="w-full bg-orange-500 hover:bg-orange-400 text-white font-bold py-3 rounded-xl transition-colors">
              Começar a usar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
